import { useId, useMemo } from "react";
import {
    isTermRecognitionMethod,
    RecognitionMethod,
} from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { formatDateOnly } from "@helpers/date";
import { CloseIcon } from "@icons";
import { ScheduleAmountInput } from "../ScheduleAmountInput";
import { buildRecognitionMethodItems } from "../scheduleRecognitionUi";
import { ScheduleManualLabeledField } from "./ScheduleManualLabeledField";
import { ScheduleRowDraft } from "./scheduleDraft";
import { ScheduleRowLabels } from "./scheduleRowLabels";
import { ScheduleRowTerm, ScheduleTermControls } from "./ScheduleTermControls";
import styles from "./ScheduleManualModal.module.scss";

interface ScheduleManualRecognitionRowProps {
    row: ScheduleRowDraft;
    /** Another row already recognizes over the term. */
    isTermMethodTaken: boolean;
    /** The schedule can no longer change: the row only shows its values. */
    isReadOnly: boolean;
    isMethodEditable: boolean;
    onAmountChange: (amount: number) => void;
    onMethodChange: (method: RecognitionMethod) => void;
    /** Omitted when the row's date cannot move, such as a changed specific date. */
    onDateChange?: (date: string) => void;
    /** Omitted on the only row. */
    onRemove?: () => void;
    term: ScheduleRowTerm;
    /** Omitted when the schedule keeps the policy's term. */
    onTermChange?: (term: ScheduleRowTerm) => void;
    minDate: string | undefined;
    portalContainer: HTMLElement | undefined;
    labels: ScheduleRowLabels;
}

export const ScheduleManualRecognitionRow = ({
    labels,
    row,
    isTermMethodTaken,
    isReadOnly,
    isMethodEditable,
    onAmountChange,
    onMethodChange,
    onDateChange,
    onRemove,
    term,
    onTermChange,
    minDate,
    portalContainer,
}: ScheduleManualRecognitionRowProps) => {
    const dateLabelId = useId();
    const isTermMethod = isTermRecognitionMethod(row.method);
    const methodItems = useMemo(
        () =>
            buildRecognitionMethodItems({
                isTermMethodTaken,
                milestoneItem:
                    row.method === RecognitionMethod.milestone ? {} : undefined,
            }),
        [isTermMethodTaken, row.method],
    );

    return (
        <HStack gap={12} align="end" wrap>
            <ScheduleManualLabeledField gap={4} label={labels.amount}>
                <ScheduleAmountInput
                    value={row.amount}
                    onValueChange={onAmountChange}
                    aria-label={labels.amount}
                    isReadOnly={isReadOnly}
                />
            </ScheduleManualLabeledField>
            <ScheduleManualLabeledField
                gap={4}
                label={isTermMethod ? "Start date" : "Date"}
                labelId={dateLabelId}
            >
                {onDateChange ? (
                    <DatePicker
                        mode="day"
                        variant="button"
                        placeholder="Select date"
                        value={row.date || undefined}
                        onValueChange={(date) => onDateChange(date ?? "")}
                        minDate={minDate}
                        showTriggerIcon={false}
                        fieldControlProps={{
                            "aria-labelledby": dateLabelId,
                            "aria-invalid":
                                row.isSpecificDate && !row.date
                                    ? true
                                    : undefined,
                        }}
                        buttonProps={{ size: "md", variant: "primary" }}
                    />
                ) : (
                    <span className={styles.readOnlyValue}>
                        {row.date ? formatDateOnly(row.date) : "—"}
                    </span>
                )}
            </ScheduleManualLabeledField>
            {isTermMethod && (
                <ScheduleManualLabeledField gap={4} label={labels.termLength}>
                    <HStack gap={6} align="center">
                        <ScheduleTermControls
                            term={term}
                            method={row.method}
                            portalContainer={portalContainer}
                            lengthAriaLabel={labels.termLength}
                            unitAriaLabel="Term unit"
                            onTermChange={onTermChange}
                        />
                    </HStack>
                </ScheduleManualLabeledField>
            )}
            <ScheduleManualLabeledField gap={4} label="Method">
                <Picker.Select
                    items={methodItems}
                    value={row.method}
                    onValueChange={(method) => {
                        if (method != null) {
                            onMethodChange(method);
                        }
                    }}
                    disabled={isReadOnly || !isMethodEditable}
                    container={portalContainer}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            aria-label="Method"
                        />
                    }
                />
            </ScheduleManualLabeledField>
            {onRemove && (
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    iconOnly
                    aria-label="Remove row"
                    onClick={onRemove}
                >
                    <CloseIcon />
                </Button>
            )}
        </HStack>
    );
};
